import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Button,
	CircularProgress,
	Alert,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
	Tooltip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TablePagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import RemoveIcon from '@mui/icons-material/Remove';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { MCPFeedback, getMCPFeedback } from '../../Services/quantiAI';

export const AIFeedbackList = () => {
	const [feedback, setFeedback] = useState<MCPFeedback[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [ratingFilter, setRatingFilter] = useState<string>('');
	const [categoryFilter, setCategoryFilter] = useState<string>('');
	const notify = useNotify();
	const navigate = useNavigate();

	const loadData = async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await getMCPFeedback({
				limit: rowsPerPage,
				offset: page * rowsPerPage,
				rating: ratingFilter || undefined,
				category: categoryFilter || undefined,
			});
			setFeedback(res.feedback || []);
			setTotal(res.total || 0);
		} catch (err: any) {
			setError(err.message || 'Erreur lors du chargement');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadData();
	}, [page, rowsPerPage, ratingFilter, categoryFilter]);

	const handleRefresh = () => {
		loadData();
		notify('Données rafraîchies', { type: 'info' });
	};

	const handleViewSession = (sessionId: string, event: React.MouseEvent) => {
		event.stopPropagation();
		navigate(`/ai-conversations/${sessionId}`);
	};

	const handleViewFeedback = (fb: MCPFeedback) => {
		navigate(`/ai-feedback/${fb.id}`, { state: { feedback: fb } });
	};

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		return date.toLocaleString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const getRatingIcon = (rating: string) => {
		switch (rating) {
			case 'positive':
				return <ThumbUpIcon color="success" fontSize="small" />;
			case 'negative':
				return <ThumbDownIcon color="error" fontSize="small" />;
			default:
				return <RemoveIcon color="disabled" fontSize="small" />;
		}
	};

	const getRatingColor = (rating: string): 'success' | 'error' | 'default' => {
		switch (rating) {
			case 'positive':
				return 'success';
			case 'negative':
				return 'error';
			default:
				return 'default';
		}
	};

	return (
		<Card>
			<Title title="Quanti AI - Feedbacks MCP" />
			<CardContent>
				{/* Actions */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						mb: 2,
						flexWrap: 'wrap',
						gap: 2,
					}}
				>
					<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
						<Button
							startIcon={<ArrowBackIcon />}
							onClick={() => navigate('/ai-conversations')}
						>
							Retour aux sessions
						</Button>
						<FormControl size="small" sx={{ minWidth: 120 }}>
							<InputLabel>Rating</InputLabel>
							<Select
								value={ratingFilter}
								label="Rating"
								onChange={(e) => {
									setRatingFilter(e.target.value);
									setPage(0);
								}}
							>
								<MenuItem value="">Tous</MenuItem>
								<MenuItem value="positive">Positif</MenuItem>
								<MenuItem value="negative">Négatif</MenuItem>
								<MenuItem value="neutral">Neutre</MenuItem>
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 150 }}>
							<InputLabel>Catégorie</InputLabel>
							<Select
								value={categoryFilter}
								label="Catégorie"
								onChange={(e) => {
									setCategoryFilter(e.target.value);
									setPage(0);
								}}
							>
								<MenuItem value="">Toutes</MenuItem>
								<MenuItem value="accuracy">Précision</MenuItem>
								<MenuItem value="relevance">Pertinence</MenuItem>
								<MenuItem value="completeness">Complétude</MenuItem>
								<MenuItem value="speed">Rapidité</MenuItem>
								<MenuItem value="other">Autre</MenuItem>
							</Select>
						</FormControl>
					</Box>
					<Button
						variant="outlined"
						startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
						onClick={handleRefresh}
						disabled={loading}
					>
						Rafraîchir
					</Button>
				</Box>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{/* Table */}
				{loading && feedback.length === 0 ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				) : feedback.length > 0 ? (
					<>
						<TableContainer component={Paper}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Rating</TableCell>
										<TableCell>Catégorie</TableCell>
										<TableCell>Utilisateur</TableCell>
										<TableCell>Projet</TableCell>
										<TableCell>Tool</TableCell>
										<TableCell>Question</TableCell>
										<TableCell>Commentaire</TableCell>
										<TableCell>Date</TableCell>
										<TableCell align="right">Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{feedback.map((fb) => (
										<TableRow
											key={fb.id}
											hover
											onClick={() => handleViewFeedback(fb)}
											sx={{ cursor: 'pointer' }}
										>
											<TableCell>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
													{getRatingIcon(fb.rating)}
													<Chip
														label={fb.rating}
														size="small"
														color={getRatingColor(fb.rating)}
													/>
												</Box>
											</TableCell>
											<TableCell>
												{fb.category ? (
													<Chip label={fb.category} size="small" variant="outlined" />
												) : (
													'-'
												)}
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{fb.user_email || `User #${fb.user_id}`}
												</Typography>
											</TableCell>
											<TableCell>
												{fb.project_folder_id ? (
													<Chip
														label={fb.project_folder_id}
														size="small"
														variant="outlined"
													/>
												) : (
													'-'
												)}
											</TableCell>
											<TableCell>
												{fb.tool_name ? (
													<Chip label={fb.tool_name} size="small" color="info" />
												) : (
													'-'
												)}
											</TableCell>
											<TableCell>
												<Typography
													variant="body2"
													sx={{
														maxWidth: 200,
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
													title={fb.original_question || undefined}
												>
													{fb.original_question || '-'}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography
													variant="body2"
													sx={{
														maxWidth: 200,
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
													title={fb.comment || undefined}
												>
													{fb.comment || '-'}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{formatDate(fb.created_at)}
												</Typography>
											</TableCell>
											<TableCell align="right">
												{fb.session_id && (
													<Tooltip title="Voir la session">
														<IconButton
															size="small"
															onClick={(e) => handleViewSession(fb.session_id!, e)}
														>
															<VisibilityIcon />
														</IconButton>
													</Tooltip>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							component="div"
							count={total}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[10, 25, 50, 100]}
							labelRowsPerPage="Par page"
						/>
					</>
				) : (
					<Alert severity="info">
						Aucun feedback enregistré. Les feedbacks des utilisateurs apparaîtront ici.
					</Alert>
				)}
			</CardContent>
		</Card>
	);
};
